import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Heart, ArrowLeft, Send, Loader2, Sparkles, Volume2, VolumeX, Mic, MicOff, Square } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SpiritChatPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  
  // Voice states
  const [playingAudio, setPlayingAudio] = useState(null); // messageIndex being played
  const [audioLoading, setAudioLoading] = useState(null); // messageIndex being loaded
  const [isRecording, setIsRecording] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    // Check if there's a saved conversation from dashboard mini chat
    const savedChat = localStorage.getItem('spiritChatSession');
    if (savedChat) {
      try {
        const { sessionId: savedSessionId, messages: savedMessages } = JSON.parse(savedChat);
        if (savedMessages && savedMessages.length > 0) {
          // Load the saved conversation
          setSessionId(savedSessionId);
          setMessages(savedMessages);
          // Clear the saved chat so it doesn't reload on next visit
          localStorage.removeItem('spiritChatSession');
          return;
        }
      } catch (e) {
        console.error("Error loading saved chat:", e);
        localStorage.removeItem('spiritChatSession');
      }
    }
    
    // Add initial Spirit greeting if no saved conversation
    setMessages([{
      role: "spirit",
      content: "Welcome, beautiful soul. I am here, always present, always listening. What weighs upon your heart today? What would you like to explore together?"
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || loading) return;

    if (!messageText) {
      setInput("");
    }
    
    // Add user message
    setMessages(prev => [...prev, { role: "user", content: textToSend }]);
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/spirit/chat`,
        { message: textToSend, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "spirit", content: response.data.response }]);
    } catch (error) {
      toast.error("Unable to connect with Spirit at this moment. Please try again.");
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
      if (!messageText) {
        setInput(textToSend);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Text-to-Speech: Play Spirit response
  const handlePlayAudio = async (messageIndex, text) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      if (playingAudio === messageIndex) {
        setPlayingAudio(null);
        return;
      }
    }

    setAudioLoading(messageIndex);
    
    try {
      const response = await axios.post(
        `${API}/spirit/tts`,
        { text },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingAudio(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        toast.error("Failed to play audio");
        setPlayingAudio(null);
        audioRef.current = null;
      };
      
      await audio.play();
      setPlayingAudio(messageIndex);
      
    } catch (error) {
      console.error("TTS error:", error);
      toast.error("Unable to generate audio. Please try again.");
    } finally {
      setAudioLoading(null);
    }
  };

  // Stop playing audio
  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingAudio(null);
    }
  };

  // Speech-to-Text: Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length === 0) {
          toast.error("No audio recorded");
          return;
        }
        
        // Process the recording
        setRecordingLoading(true);
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          const response = await axios.post(
            `${API}/spirit/stt`,
            audioBlob,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'audio/webm'
              }
            }
          );
          
          if (response.data.text) {
            setInput(response.data.text);
            toast.success("Voice captured! Review and send your message.");
          } else {
            toast.error("Could not understand audio. Please try again.");
          }
        } catch (error) {
          console.error("STT error:", error);
          toast.error("Failed to transcribe audio. Please try again.");
        } finally {
          setRecordingLoading(false);
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Recording... Speak now");
      
    } catch (error) {
      console.error("Microphone error:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  // Speech-to-Text: Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const suggestedPrompts = [
    "How do I learn to forgive myself?",
    "I feel lost. How do I find my way?",
    "Help me understand my worth",
    "What does my soul need right now?"
  ];

  return (
    <div className="min-h-screen hero-gradient flex flex-col" data-testid="spirit-chat-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="spirit-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center animate-breathe">
              <Heart className="w-4 h-4 text-primary" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold">Speak with Spirit</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      {/* Chat Container */}
      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full">
        {/* Messages */}
        <ScrollArea className="flex-1 px-6 py-4" ref={scrollRef}>
          <div className="space-y-6 pb-4">
            {messages.map((message, index) => (
              <div
                key={`msg-${index}-${message.role}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`message-${index}`}
              >
                {message.role === "spirit" && (
                  <div className="flex items-start gap-3 max-w-[85%]">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="spirit-response">
                      <p className="spirit-message text-foreground leading-relaxed">
                        {message.content}
                      </p>
                      {/* Listen button for Spirit messages */}
                      <div className="mt-2 flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => playingAudio === index ? handleStopAudio() : handlePlayAudio(index, message.content)}
                          disabled={audioLoading === index}
                          className="text-xs text-muted-foreground hover:text-primary h-7 px-2"
                          data-testid={`listen-btn-${index}`}
                        >
                          {audioLoading === index ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Loading...
                            </>
                          ) : playingAudio === index ? (
                            <>
                              <VolumeX className="w-3 h-3 mr-1" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-3 h-3 mr-1" />
                              Listen
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {message.role === "user" && (
                  <div className="user-message max-w-[85%]">
                    <p className="text-foreground">{message.content}</p>
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex items-start gap-3" data-testid="spirit-loading">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-primary animate-pulse" strokeWidth={1.5} />
                </div>
                <div className="spirit-response">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="italic">Spirit is connecting...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Suggested Prompts (only show if few messages) */}
        {messages.length <= 2 && !loading && (
          <div className="px-6 pb-4">
            <p className="text-sm text-muted-foreground mb-3">Or try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt, index) => (
                <Button
                  key={prompt}
                  variant="outline"
                  size="sm"
                  onClick={() => setInput(prompt)}
                  className="text-sm rounded-full bg-white/50 dark:bg-white/10 border-border hover:bg-white/80 dark:hover:bg-white/20"
                  data-testid={`suggested-prompt-${index}`}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-border/50 bg-background/80 backdrop-blur-md p-4">
          <div className="max-w-3xl mx-auto">
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-3">
                <div className="flex items-end gap-3">
                  {/* Microphone Button */}
                  <Button
                    variant="ghost"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={loading || recordingLoading}
                    className={`rounded-full w-12 h-12 p-0 flex-shrink-0 ${
                      isRecording 
                        ? 'bg-red-500 hover:bg-red-600 text-white' 
                        : 'hover:bg-primary/10'
                    }`}
                    data-testid="mic-btn"
                    title={isRecording ? "Stop recording" : "Speak to Spirit"}
                  >
                    {recordingLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isRecording ? (
                      <Square className="w-5 h-5" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                  </Button>
                  
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isRecording ? "Recording... Speak now" : "Share what's on your heart..."}
                    className="min-h-[50px] max-h-[150px] bg-transparent border-0 resize-none focus:ring-0 text-foreground placeholder:text-muted-foreground/50"
                    disabled={loading || isRecording}
                    data-testid="spirit-input"
                  />
                  <Button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || loading || isRecording}
                    className="btn-primary rounded-full w-12 h-12 p-0 flex-shrink-0"
                    data-testid="spirit-send-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <p className="text-xs text-center text-muted-foreground mt-3">
              <Mic className="w-3 h-3 inline mr-1" />
              Tap the mic to speak • <Volume2 className="w-3 h-3 inline mx-1" /> Tap Listen to hear responses
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritChatPage;
